import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase/client';
import type { WhatsAppNumber, WhatsAppConfig } from '../types';

export const useWhatsApp = () => {
  const [numbers, setNumbers] = useState<WhatsAppNumber[]>([]);
  const [config, setConfig] = useState<WhatsAppConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNumbers();
    fetchConfig();
  }, []);

  const fetchNumbers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('whatsapp_numbers')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching WhatsApp numbers:', error);
    } else {
      setNumbers(data || []);
    }
    setLoading(false);
  };

  const fetchConfig = async () => {
    const { data, error } = await supabase
      .from('whatsapp_config')
      .select('*')
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching WhatsApp config:', error);
    } else {
      setConfig(data);
    }
  };

  const createNumber = async (numberData: Omit<WhatsAppNumber, 'id' | 'created_at' | 'updated_at' | 'user_id'>) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('whatsapp_numbers')
      .insert([{ ...numberData, user_id: user.id }])
      .select()
      .single();

    if (error) throw error;
    setNumbers(prev => [data, ...prev]);
    return data;
  };

  const updateNumber = async (id: string, updates: Partial<WhatsAppNumber>) => {
    const { data, error } = await supabase
      .from('whatsapp_numbers')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    setNumbers(prev => prev.map(n => n.id === id ? data : n));
    return data;
  };

  const deleteNumber = async (id: string) => {
    const { error } = await supabase
      .from('whatsapp_numbers')
      .delete()
      .eq('id', id);

    if (error) throw error;
    setNumbers(prev => prev.filter(n => n.id !== id));
  };

  const saveConfig = async (configData: Omit<WhatsAppConfig, 'id' | 'user_id'>) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    if (config) {
      const { data, error } = await supabase
        .from('whatsapp_config')
        .update(configData)
        .eq('id', config.id)
        .select()
        .single();

      if (error) throw error;
      setConfig(data);
      return data;
    } else {
      const { data, error } = await supabase
        .from('whatsapp_config')
        .insert([{ ...configData, user_id: user.id }])
        .select()
        .single();

      if (error) throw error;
      setConfig(data);
      return data;
    }
  };

  const validatePhoneNumber = (phone: string): { valid: boolean; formatted?: string; message?: string } => {
    const cleaned = phone.replace(/\D/g, '');
    
    if (cleaned.length < 10 || cleaned.length > 15) {
      return { valid: false, message: 'Número deve ter entre 10 e 15 dígitos' };
    }

    if (!cleaned.startsWith('55') && !cleaned.startsWith('+55')) {
      return { valid: false, message: 'Para números brasileiros, use o código do país (55)' };
    }

    return { valid: true, formatted: cleaned.startsWith('+') ? cleaned : `+${cleaned}` };
  };

  const isConfigured = () => {
    return !!(config?.api_token && config?.phone_number_id);
  };

  return {
    numbers,
    config,
    loading,
    createNumber,
    updateNumber,
    deleteNumber,
    saveConfig,
    fetchNumbers,
    fetchConfig,
    validatePhoneNumber,
    isConfigured,
  };
};
